import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/apiService';

// Página admin: quem pode fazer o quê nos dois MCPs (mcp.alia.do e mcp-qa.alia.do).
// Consome /api/mcp/permissions, que passa pelo apidash e pergunta aos DOIS servidores.
//
// ESCREVE, desde 03/09/2026. As duas objeções que a mantinham só-leitura caíram:
//
//  1. "alguém se auto-concederia escrita em produção" — conceder exige o tema
//     `permission_admin`, que NÃO é concedível por aqui. Para entrar nele é preciso o .env
//     da caixa ou um INSERT no store. A porta não se abre por dentro, e é o servidor que
//     recusa, não esta tela: os controles sumirem é conforto, não a trava.
//  2. "as listas nem estão no Parameter Store" — não estão mesmo, e agora nem lá deveriam:
//     a fonte da verdade é `internal_mcp.permission_events`, um store por ambiente. O .env
//     virou semente de caixa nova.
//
// O que esta tela mostra e o Parameter Store não mostraria: a matriz EFETIVA, lida do
// processo em execução. É o que pega .env editado à mão e load-prod-params que não rodou.
//
// ATENÇÃO ao abrir de admin-qa/admin-hmg: o DASHAPI_URL aponta para o apidash de PRODUÇÃO
// em todo ambiente, então esta tela mexe nos MCPs de verdade, venha de onde vier. Não
// existe "permissão de mentira" aqui.

const ENVS = [
  // Mesmos nomes da tela de auditoria: são três servidores, não três ambientes de um.
  { id: 'prod', nome: 'MCP Interno', host: 'mcp.alia.do', cor: '#b91c1c' },
  { id: 'qa', nome: 'MCP Interno QA', host: 'mcp-qa.alia.do', cor: '#2563eb' },
];

function fmtData(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }); }
  catch { return iso; }
}

export default function McpPermissoes() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [soDivergencias, setSoDivergencias] = useState(false);
  const [aberto, setAberto] = useState(null);   // e-mail com o detalhe expandido

  const carregar = useCallback(async () => {
    setLoading(true); setErro(null);
    try {
      const d = await apiService.getMcpPermissions();
      // Resposta fora de forma é ERRO, não dado. O `routes/api.js` não é passthrough: uma
      // rota que falte faz o browser receber 200 com o corpo de outra coisa, e sem esta
      // conferência a tela estouraria num `.map()` de undefined em vez de dizer o que houve.
      if (!d || !Array.isArray(d.pessoas) || !d.ambientes) {
        throw new Error('resposta inesperada do servidor (rota do BFF registrada?)');
      }
      setDados(d);
    } catch (e) {
      setErro(e?.response?.data?.error || e.message || 'falha ao carregar');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // ─── Edição ───────────────────────────────────────────────────────────────
  // `editando` = { env, email }. Os marcados ficam num Set local até Salvar: aplicar
  // checkbox a checkbox mandaria uma chamada por clique e deixaria estado pela metade se
  // uma falhasse no meio.
  const [editando, setEditando] = useState(null);
  const [marcados, setMarcados] = useState(new Set());
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState(null);   // { tipo: 'ok'|'erro', texto }
  const [novoEmail, setNovoEmail] = useState('');
  const [mostrarSemAcesso, setMostrarSemAcesso] = useState(false);
  const [avisoBloqueio, setAvisoBloqueio] = useState(false);
  const [busca, setBusca] = useState('');

  const euPossoConceder = useCallback((envId) => {
    // A MESMA regra do servidor: ter `permission_admin` naquele ambiente. Não é a trava —
    // o MCP recusa de qualquer jeito —, é para não oferecer um botão que sempre dá 400.
    const a = dados?.ambientes?.[envId];
    const t = (a?.temas || []).find(x => x.chave === 'permission_admin');
    return !!(dados?.eu && t && t.emails.includes(dados.eu));
  }, [dados]);

  // Escrever só vale de admin.alia.do. A tela fica IDÊNTICA nos três ambientes — mesma
  // matriz, mesmos botões —, e o aviso vem no clique, antes de a pessoa preencher motivo e
  // marcar tema à toa. A trava mesmo é o POST do BFF (`ehAdminDeProducao`); isto aqui só
  // evita a viagem perdida.
  const bloqueadoAqui = () => {
    if (dados?.escrita_permitida_daqui !== false) return false;
    setAvisoBloqueio(true);
    return true;
  };

  const abrirEdicao = (envId, email) => {
    if (bloqueadoAqui()) return;
    const a = dados.ambientes[envId];
    const atual = (a.pessoas_temas || []).find(p => p.email === email);
    setEditando({ env: envId, email });
    setMarcados(new Set([...(atual?.temas || []), ...(atual?.tools || []).map(t => `tool:${t}`)]));
    setMotivo(''); setRecado(null); setBusca('');
  };

  const alternar = (chave) => setMarcados(prev => {
    const n = new Set(prev);
    if (n.has(chave)) n.delete(chave); else n.add(chave);
    return n;
  });

  const salvar = async () => {
    const { env, email } = editando;
    const a = dados.ambientes[env];
    const atual = (a.pessoas_temas || []).find(p => p.email === email);
    const antes = new Set([...(atual?.temas || []),
                           ...(atual?.tools || []).map(t => `tool:${t}`)]);
    const item = (c) => c.startsWith('tool:') ? { tool: c.slice(5) } : { tema: c };
    // Só o DELTA vai para o servidor. Mandar o conjunto inteiro geraria um evento por tema
    // a cada salvamento, e a trilha (que é append-only) viraria ruído em duas semanas.
    const conceder = [...marcados].filter(c => !antes.has(c)).map(item);
    const revogar = [...antes].filter(c => !marcados.has(c)).map(item);
    if (!conceder.length && !revogar.length) {
      setRecado({ tipo: 'erro', texto: 'nada mudou.' }); return;
    }
    setSalvando(true); setRecado(null);
    try {
      await apiService.setMcpPermissions({ env, email, conceder, revogar, motivo });
      setEditando(null);
      await carregar();
      setRecado({ tipo: 'ok',
                  texto: `${email}: ${conceder.length} concedido(s), ${revogar.length} revogado(s) em ${env}.` });
    } catch (e) {
      // O texto vem do MCP e é escrito para quem clicou ("você não tem o tema X").
      setRecado({ tipo: 'erro', texto: e?.response?.data?.error || e.message || 'falhou' });
    } finally { setSalvando(false); }
  };

  const S = {
    wrap: { maxWidth: 1200, margin: '0 auto', padding: '1.5rem' },
    h1: { fontSize: 22, margin: 0 },
    sub: { color: '#666', fontSize: 13, marginTop: 4 },
    card: { background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: 16, marginTop: 16 },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 16 },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: 12 },
    th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #eee', fontSize: 13, color: '#555' },
    td: { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', fontSize: 14, verticalAlign: 'top' },
    tag: { display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, marginRight: 4, marginBottom: 3 },
    btn: { padding: '6px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13 },
    aviso: { background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginTop: 6, color: '#7c2d12' },
    erro: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#991b1b' },
    mono: { fontFamily: 'ui-monospace,monospace', fontSize: 12 },
    // Lista de tools: bullets em vez de texto corrido. O nome da tool é o que
    // identifica o que a permissão realmente libera — escondido num tooltip,
    // ninguém lia antes de conceder.
    tools: { margin: '4px 0 0', paddingLeft: 18, color: '#555' },
    toolItem: { fontFamily: 'ui-monospace,monospace', fontSize: 12, lineHeight: 1.7 },
    ok: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', color: '#166534' },
    btnP: { padding: '6px 12px', borderRadius: 8, border: 0, background: '#1d4ed8', color: '#fff', cursor: 'pointer', fontSize: 13 },
    // zIndex 1100: o cabeçalho do admin é sticky com z-index 1000 (Header.css). Com 50, o
    // cabeçalho ficava POR CIMA do fundo escuro — sem esmaecer e cortando o topo da caixa.
    // Mesmo valor do Dialogo.css e do .vr-modal-overlay. Ver "Camadas (z-index)" no CLAUDE.md.
    modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '24px 16px' },
    caixa: { background: '#fff', borderRadius: 12, padding: 20, maxWidth: 720, width: '100%', maxHeight: 'calc(100vh - 48px)', overflowY: 'auto' },
    busca: { position: 'sticky', top: -20, zIndex: 1, background: '#fff', padding: '12px 0 8px', margin: '0 0 4px', borderBottom: '1px solid #f0f0f0' },
    linha: { display: 'flex', gap: 8, alignItems: 'flex-start', padding: '7px 0', borderBottom: '1px solid #f5f5f5' },
    input: { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, marginTop: 4 },
    caixaAviso: { background: '#fff', borderRadius: 12, padding: 24, maxWidth: 480, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,.2)' },
    selo: { width: 40, height: 40, borderRadius: 10, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 },
    link: { color: '#1d4ed8', fontWeight: 600, textDecoration: 'none' },
  };

  if (loading) return <div style={S.wrap}>Carregando…</div>;
  if (erro) return <div style={S.wrap}><div style={S.erro}>Erro: {erro}</div></div>;

  // Quem tem ALGUM acesso, em algum ambiente. A resposta traz a união de "admin em
  // core.users" com "está em alguma allowlist", e a primeira metade é quase toda conta de
  // cliente (leroyadmin, mmdash, magaluadmin) que nunca vai usar o MCP: catorze linhas de
  // ruído escondendo as poucas que importam. A união continua vindo do servidor, e o
  // interruptor abaixo a traz de volta — é ela que responde "fulano deveria ter acesso e
  // não tem?".
  const temAcesso = (p) => ENVS.some(env => {
    const m = (dados.ambientes?.[env.id]?.pessoas_temas || []).find(x => x.email === p.email);
    return !!m && (m.temas.length > 0 || m.tools.length > 0);
  });
  const comAcesso = dados.pessoas.filter(temAcesso);
  const semAcesso = dados.pessoas.length - comAcesso.length;
  const base = mostrarSemAcesso ? dados.pessoas : comAcesso;
  const pessoas = soDivergencias ? base.filter(p => (p.avisos || []).length > 0) : base;
  const comDivergencia = base.filter(p => (p.avisos || []).length > 0).length;

  // ── Temas dos dois MCPs, cruzados por chave ──────────────────────────────
  // A tabela é por TEMA e não por ambiente, então precisa do índice cruzado. Ambiente
  // indisponível (QA dorme 01:00–06:00) não some da tabela: ele entra sem temas, e a
  // coluna dele mostra "—". Sumir faria a tela afirmar que o tema é exclusivo do outro.
  // Descrição de tema é um parágrafo — na coluna vira parede de texto. Corta na
  // primeira frase, e no talo se a frase for longa. O texto inteiro fica no `title`,
  // para quem quiser passar o mouse, e no modal de conceder.
  const resumo = (txt) => {
    const t = String(txt || '').trim();
    if (!t) return '';
    const ponto = t.indexOf('. ');
    const frase = ponto > 0 ? t.slice(0, ponto + 1) : t;
    return frase.length > 120 ? frase.slice(0, 117).trimEnd() + '…' : frase;
  };

  const temaDe = (envId, chave) =>
    (dados.ambientes?.[envId]?.temas || []).find(t => t.chave === chave) || null;

  const chavesDe = envId => (dados.ambientes?.[envId]?.temas || []).map(t => t.chave);
  const chavesProd = chavesDe('prod');
  const chavesQa = chavesDe('qa');
  const setQa = new Set(chavesQa);
  const setProd = new Set(chavesProd);
  // Ordem: a de produção manda nos comuns, para a lista não dançar entre deploys.
  const grupos = [
    { rotulo: 'Nos dois ambientes', chaves: chavesProd.filter(c => setQa.has(c)) },
    { rotulo: 'Só em produção', chaves: chavesProd.filter(c => !setQa.has(c)) },
    { rotulo: 'Só em QA', chaves: chavesQa.filter(c => !setProd.has(c)) },
  ].filter(g => g.chaves.length > 0);

  return (
    <div style={S.wrap}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h1 style={S.h1}>Permissões do MCP</h1>
          <div style={S.sub}>
            O que cada pessoa pode fazer em <strong>mcp.alia.do</strong> e{' '}
            <strong>mcp-qa.alia.do</strong>. Lido dos próprios servidores — é o que eles
            aplicam agora, não o que foi publicado. Conceder e revogar vale na chamada
            seguinte, sem reiniciar nada.
          </div>
        </div>
        <button style={S.btn} onClick={carregar}>Recarregar</button>
      </div>

      {recado && (
        <div style={{ ...(recado.tipo === 'ok' ? S.ok : S.erro), marginTop: 12 }}>
          {recado.texto}
        </div>
      )}

      {/* Estado de cada MCP: só o que é FATO DO AMBIENTE (indisponível, fonte da
          permissão, escrita desligada, tool órfã). Fica fora da tabela de temas de
          propósito — a tabela agora é por TEMA, e um aviso de ambiente repetido em
          cada linha viraria ruído. */}
      <div style={{ ...S.grid, marginTop: 16 }}>
        {ENVS.map(env => {
          const a = dados.ambientes?.[env.id] || {};
          const orfas = a.tools_sem_escopo || [];
          const escoposComErro = (a.escopos || []).filter(e => e.erro);
          return (
            <div key={env.id} style={S.card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ ...S.tag, background: env.cor, color: '#fff' }}>{env.nome}</span>
                <span style={S.mono}>{env.host}</span>
              </div>
              {a.indisponivel ? (
                <div style={S.aviso}>
                  <strong>Indisponível.</strong> {a.indisponivel}
                  {a.dica ? <div style={{ marginTop: 4 }}>{a.dica}</div> : null}
                </div>
              ) : (
                <>
                  <div style={{ ...S.sub, marginTop: 8 }}>
                    {a.total_tools} tools · domínio exigido: {a.allowed_email_domain || '(nenhum)'}
                  </div>
                  {a.fonte_permissoes && a.fonte_permissoes !== 'store' && (
                    <div style={S.aviso}>
                      <strong>Permissões vindas de "{a.fonte_permissoes}", não do banco.</strong>{' '}
                      {a.fonte_permissoes === 'semente'
                        ? 'O store não respondeu desde o boot e o .env está valendo — conceder aqui NÃO vai ter efeito.'
                        : 'O store caiu depois de ler; vale o último estado bom, e mudanças não entram.'}
                      {a.erro_permissoes ? <div style={S.mono}>{a.erro_permissoes}</div> : null}
                    </div>
                  )}
                  {a.escrita_habilitada === false && (
                    <div style={S.aviso}>
                      Só leitura: este MCP está sem <span style={S.mono}>MCP_PERMISSIONS_WRITE_TOKEN</span>.
                    </div>
                  )}
                  {escoposComErro.length > 0 && (
                    <div style={S.aviso}>
                      {escoposComErro.map(e => <div key={e.chave}>{e.erro}</div>)}
                    </div>
                  )}
                  {orfas.length > 0 && (
                    <div style={S.aviso}>
                      <strong>{orfas.length} tool(s) sem tema — ninguém consegue chamá-las.</strong>{' '}
                      {orfas.map(t => t.tool).join(', ')}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Temas, com os dois ambientes lado a lado ────────────────────────
          O eixo é o TEMA, não o ambiente: a pergunta da tela é "quem tem X", e
          antes ela obrigava a comparar dois cartões distantes para responder.

          ATENÇÃO, e é o motivo dos avisos de divergência abaixo: MESMA CHAVE NÃO
          É A MESMA PERMISSÃO. `leitura` em produção devolve `anon_transcript`
          DESCRIPTOGRAFADO da réplica; em QA é o banco de QA. `fases_escrita` em
          produção é só `resize_phase`; em QA é `apply_phase_change`, treze
          entidades. Pôr lado a lado facilita a leitura e, por isso mesmo, cria o
          risco de alguém concluir que conceder nos dois é a mesma coisa. Quando o
          conjunto de tools difere, a linha diz. */}
      <div style={S.card}>
        <div><strong>Temas</strong></div>
        <div style={S.sub}>
          Comuns aos dois MCPs primeiro, depois o que só existe em cada um.
          Mesma chave nos dois ambientes <strong>não</strong> quer dizer mesmo alcance —
          compare as tools.
        </div>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={{ ...S.th, width: '40%' }}>Tema</th>
              {ENVS.map(env => (
                <th key={env.id} style={S.th}>
                  <span style={{ ...S.tag, background: env.cor, color: '#fff' }}>{env.nome}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grupos.map(g => (
              <React.Fragment key={g.rotulo}>
                <tr>
                  <td colSpan={1 + ENVS.length}
                      style={{ ...S.td, background: '#fafafa', fontSize: 12, color: '#666',
                               textTransform: 'uppercase', letterSpacing: .4 }}>
                    {g.rotulo}
                  </td>
                </tr>
                {g.chaves.map(chave => {
                  const porEnv = {};
                  ENVS.forEach(e => { porEnv[e.id] = temaDe(e.id, chave); });
                  // Identidade da linha: quem existir. Título/descrição podem divergir
                  // entre ambientes — quando divergem, mostramos o do ambiente na coluna.
                  const ref = porEnv.prod || porEnv.qa;
                  const presentes = ENVS.map(e => porEnv[e.id]).filter(Boolean);
                  const toolsDiferem = presentes.length === 2
                    && presentes[0].tools.join(',') !== presentes[1].tools.join(',');
                  const tituloDifere = presentes.length === 2
                    && presentes[0].titulo !== presentes[1].titulo;
                  return (
                    <tr key={chave}>
                      <td style={S.td}>
                        <div>
                          {/* Título encabeça a linha quando os dois ambientes concordam.
                              Divergindo, entra a chave — e os dois nomes vêm logo abaixo,
                              cada um com o selo do seu ambiente. Eleger um deles escondia
                              o outro (foi o que fez as fases de QA parecerem sumidas). */}
                          {tituloDifere
                            ? <span style={S.mono}>{chave}</span>
                            : ref.titulo}
                          {' '}
                          {ref.escrita && (
                            <span style={{ ...S.tag, background: '#fee2e2', color: '#991b1b', marginLeft: 6 }}>
                              escreve
                            </span>
                          )}
                          {!ref.concedivel && (
                            <span style={{ ...S.tag, background: '#e5e7eb', color: '#374151', marginLeft: 4 }}
                                  title="não é concedível por esta tela, de propósito">
                              fora da tela
                            </span>
                          )}
                        </div>
                        {!tituloDifere && (
                          <div style={{ ...S.sub, marginTop: 2 }}>{ref.descricao}</div>
                        )}
                        {/* Uma vez só: divergindo, a chave já encabeça a linha. */}
                        {!tituloDifere && (
                          <div style={{ ...S.mono, color: '#999', marginTop: 4 }}>{chave}</div>
                        )}
                        {/* O aviso só sobra para o caso em que os títulos são IGUAIS e
                            mesmo assim as tools divergem — aí nada na tela denunciaria.
                            Com títulos diferentes, os dois nomes acima já são o aviso. */}
                        {toolsDiferem && !tituloDifere && (
                          <div style={{ ...S.sub, marginTop: 4, color: '#7c2d12' }}>
                            ⚠ mesma chave e mesmo título, mas as tools diferem entre os ambientes.
                          </div>
                        )}
                      </td>
                      {ENVS.map(env => {
                        const t = porEnv[env.id];
                        const a = dados.ambientes?.[env.id] || {};
                        if (a.indisponivel) {
                          return <td key={env.id} style={{ ...S.td, color: '#999' }}>—</td>;
                        }
                        if (!t) {
                          // Ausência é informação: o tema não existe neste MCP, então não
                          // há o que conceder. Diferente de "existe e ninguém tem".
                          return (
                            <td key={env.id} style={{ ...S.td, color: '#999' }}>
                              <span style={S.sub}>não existe neste MCP</span>
                            </td>
                          );
                        }
                        return (
                          <td key={env.id} style={S.td}>
                            {/* Divergindo, o nome e o resumo DESTE ambiente encabeçam a
                                célula — é o que a pessoa procura quando quer "fases em QA".
                                Resumo, não a descrição inteira: o parágrafo completo aqui
                                transformava a linha numa parede de texto. */}
                            {tituloDifere && (
                              <div style={{ fontWeight: 600 }}>{t.titulo}</div>
                            )}
                            {tituloDifere && (
                              <div style={{ ...S.sub, marginTop: 2, marginBottom: 8 }}
                                   title={t.descricao}>
                                {resumo(t.descricao)}
                              </div>
                            )}
                            {t.emails.length
                              ? t.emails.map(m => <div key={m} style={S.mono}>{m}</div>)
                              : <span style={{ color: '#b91c1c' }}>ninguém</span>}
                            <div style={{ ...S.sub, marginTop: 6 }}>
                              {t.tools.length} tool{t.tools.length === 1 ? '' : 's'}
                            </div>
                            <ul style={S.tools}>
                              {t.tools.map(n => <li key={n} style={S.toolItem}>{n}</li>)}
                            </ul>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <strong>Pessoas</strong>
            <span style={S.sub}>
              {'  '}quem tem algum acesso a um dos dois MCPs
            </span>
          </div>
          <div style={{ display: 'flex', gap: 14 }}>
            <label style={{ fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={mostrarSemAcesso}
                     onChange={e => setMostrarSemAcesso(e.target.checked)} />
              {' '}mostrar quem não tem acesso ({semAcesso})
            </label>
            <label style={{ fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={soDivergencias}
                     onChange={e => setSoDivergencias(e.target.checked)} />
              {' '}só divergências ({comDivergencia})
            </label>
          </div>
        </div>

        {/* A tabela abaixo é a união de "admin em core.users" com "está em alguma allowlist".
            Quem não é nenhum dos dois não tem linha — e é justamente quem mais precisa
            receber acesso da primeira vez. Sem esta entrada, não haveria como conceder a
            uma pessoa nova sem antes torná-la admin, que é um acesso bem maior. */}
        {ENVS.some(e => euPossoConceder(e.id)) && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10,
                        flexWrap: 'wrap' }}>
            <span style={S.sub}>Conceder a quem ainda não aparece:</span>
            <input style={{ ...S.input, width: 260, marginTop: 0 }} value={novoEmail}
                   placeholder="alguem@alia.do"
                   onChange={ev => setNovoEmail(ev.target.value)} />
            {ENVS.filter(e => euPossoConceder(e.id)
                              && dados.ambientes?.[e.id]?.escrita_habilitada
                              && !dados.ambientes?.[e.id]?.indisponivel).map(e => (
              <button key={e.id} style={S.btn}
                      disabled={!novoEmail.includes('@')}
                      onClick={() => abrirEdicao(e.id, novoEmail.trim().toLowerCase())}>
                em {e.nome}
              </button>
            ))}
          </div>
        )}

        {/* Por padrão, só quem TEM acesso. A permissão do MCP é por e-mail e não depende de
            core.users, então dá para estar aqui sem ser admin do banco — e esse caso continua
            aparecendo, com o aviso. Quem não tem acesso nenhum sai da lista, e volta pelo
            interruptor acima. */}
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Pessoa</th>
              <th style={S.th}>core.users</th>
              <th style={S.th}>Produção</th>
              <th style={S.th}>QA</th>
            </tr>
          </thead>
          <tbody>
            {pessoas.map(p => {
              const temAviso = (p.avisos || []).length > 0;
              return (
                <React.Fragment key={p.email}>
                  <tr onClick={() => setAberto(aberto === p.email ? null : p.email)}
                      style={{ cursor: 'pointer' }}>
                    <td style={S.td}>
                      <span style={S.mono}>{p.email}</span>
                      {temAviso ? <span style={{ ...S.tag, background: '#fed7aa', color: '#7c2d12', marginLeft: 6 }}>!</span> : null}
                    </td>
                    <td style={S.td}>
                      {p.core_user
                        ? <>#{p.core_user.user_id} · {p.core_user.fullname}</>
                        : <span style={{ color: '#b45309' }}>não é admin</span>}
                    </td>
                    {ENVS.map(env => {
                      const a = dados.ambientes?.[env.id] || {};
                      const meus = (a.pessoas_temas || []).find(x => x.email === p.email);
                      const podeEditar = !a.indisponivel && a.escrita_habilitada
                                         && euPossoConceder(env.id);
                      return (
                        <td style={S.td} key={env.id}>
                          {/* Os TEMAS, que é a unidade que se concede. Sem repetir o #id
                              aqui: desde a Fase 2 ele é DERIVADO de core.users, então é o
                              mesmo número da coluna ao lado — e um id divergente, que era o
                              que valia a pena mostrar, deixou de ser possível. */}
                          {!meus || (!meus.temas.length && !meus.tools.length)
                            ? <span style={{ color: '#999' }}>—</span>
                            : <>
                                {meus.temas.map(chave => {
                                  const t = (a.temas || []).find(x => x.chave === chave) || {};
                                  return (
                                    <span key={chave}
                                          style={{ ...S.tag,
                                                   background: t.escrita ? '#fee2e2' : '#eef2ff',
                                                   color: t.escrita ? '#991b1b' : '#3730a3' }}
                                          title={`${chave} · ${(t.tools || []).length} tools`}>
                                      {t.titulo || chave}
                                    </span>
                                  );
                                })}
                                {meus.tools.map(tool => (
                                  <span key={tool} style={{ ...S.tag, background: '#fef9c3', color: '#854d0e' }}
                                        title="tool avulsa, concedida fora de qualquer tema">
                                    {tool}
                                  </span>
                                ))}
                              </>}
                          {podeEditar && (
                            <div>
                              <button style={{ ...S.btn, padding: '2px 8px', fontSize: 11, marginTop: 4 }}
                                      onClick={(ev) => { ev.stopPropagation(); abrirEdicao(env.id, p.email); }}>
                                editar
                              </button>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {aberto === p.email && temAviso && (
                    <tr>
                      <td style={S.td} colSpan={4}>
                        {p.avisos.map((a, i) => <div key={i} style={S.aviso}>{a}</div>)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {dados.nota ? <div style={{ ...S.sub, marginTop: 10 }}>{dados.nota}</div> : null}
      </div>

      {avisoBloqueio && (
        <div style={S.modal} onClick={() => setAvisoBloqueio(false)}>
          <div style={S.caixaAviso} onClick={ev => ev.stopPropagation()}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={S.selo}>🔒</div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>
                  Alterar permissão só em produção
                </div>
                <div style={{ ...S.sub, marginTop: 8, lineHeight: 1.5 }}>
                  Este ambiente é <strong>somente leitura</strong> para permissões. Para
                  conceder ou revogar, abra{' '}
                  <span style={S.mono}>{dados.host_de_escrita || 'admin.alia.do'}</span>.
                </div>
                <div style={{ ...S.sub, marginTop: 10, lineHeight: 1.5 }}>
                  Os três admins falam com o <strong>mesmo</strong> serviço e alcançam os dois
                  MCPs — daqui você estaria mudando produção sem perceber. Consultar quem pode
                  o quê continua liberado.
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <a style={{ ...S.btn, ...S.link, display: 'inline-block' }}
                 href={`https://${dados.host_de_escrita || 'admin.alia.do'}/mcp/permissoes`}
                 target="_blank" rel="noreferrer"
                 onClick={() => setAvisoBloqueio(false)}>
                Abrir em produção
              </a>
              <button style={S.btnP} onClick={() => setAvisoBloqueio(false)}>Entendi</button>
            </div>
          </div>
        </div>
      )}

      {editando && (() => {
        const a = dados.ambientes[editando.env];
        const env = ENVS.find(e => e.id === editando.env);
        const atual = (a.pessoas_temas || []).find(x => x.email === editando.email);
        const antes = new Set([...(atual?.temas || []),
                               ...(atual?.tools || []).map(t => `tool:${t}`)]);
        const mudou = [...marcados].some(c => !antes.has(c))
                   || [...antes].some(c => !marcados.has(c));
        // Busca por tema OU tool, sem acento e sem caixa. Tema que casa pelo título, chave ou
        // descrição aparece inteiro; senão aparece se alguma tool dele casa. Filtrar não
        // desmarca nada: o que está marcado fora do filtro continua indo no Salvar.
        const norm = x => String(x || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const q = norm(busca.trim());
        const temaCasa = t => !q || [t.titulo, t.chave, t.descricao].some(x => norm(x).includes(q));
        const toolsQueCasam = t => (temaCasa(t) ? t.tools : t.tools.filter(n => norm(n).includes(q)));
        const temasVisiveis = (a.temas || []).filter(t => temaCasa(t) || toolsQueCasam(t).length);
        const ocultosMarcados = q ? [...marcados].filter(c => c.startsWith('tool:')
          ? !temasVisiveis.some(t => toolsQueCasam(t).includes(c.slice(5)))
          : !temasVisiveis.some(t => t.chave === c)).length : 0;
        return (
          <div style={S.modal} onClick={() => !salvando && setEditando(null)}>
            <div style={S.caixa} onClick={ev => ev.stopPropagation()}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ ...S.tag, background: env.cor, color: '#fff' }}>{env.nome}</span>
                <strong style={S.mono}>{editando.email}</strong>
              </div>
              <div style={{ ...S.sub, marginTop: 6 }}>
                Marque o que a pessoa deve ter. Vale na chamada seguinte — sem reiniciar nada.
                Sem <span style={S.mono}>Entrar no servidor</span> ela não usa nada, nem as
                leituras.
              </div>

              <div style={S.busca}>
                <input id="mcp-perm-busca" type="search" style={{ ...S.input, marginTop: 0 }} value={busca}
                       onChange={ev => setBusca(ev.target.value)} autoFocus
                       placeholder="Buscar tema ou tool (ex.: whitelabel, sync_items)" />
                {q && (
                  <div style={{ ...S.sub, marginTop: 6 }}>
                    {temasVisiveis.length} de {(a.temas || []).length} temas
                    {ocultosMarcados > 0 && <> · {ocultosMarcados} marcado(s) fora do filtro continua(m) marcado(s)</>}
                  </div>
                )}
              </div>

              {q && temasVisiveis.length === 0 && (
                <div style={{ ...S.sub, padding: '12px 0' }}>Nenhum tema ou tool com “{busca.trim()}”.</div>
              )}

              {temasVisiveis.map(t => (
                <label key={t.chave}
                       style={{ ...S.linha, opacity: t.concedivel ? 1 : .55,
                                cursor: t.concedivel ? 'pointer' : 'not-allowed' }}>
                  <input type="checkbox" disabled={!t.concedivel || salvando}
                         checked={marcados.has(t.chave)}
                         onChange={() => alternar(t.chave)} style={{ marginTop: 3 }} />
                  <span style={{ flex: 1 }}>
                    <span>{t.titulo}</span>
                    {t.escrita && (
                      <span style={{ ...S.tag, background: '#fee2e2', color: '#991b1b', marginLeft: 6 }}>
                        escreve
                      </span>
                    )}
                    <div style={S.sub}>{t.descricao}</div>
                    <div style={{ ...S.mono, color: '#999' }}>
                      {t.chave} · {t.tools.length} tool(s)
                      {!t.concedivel && ' · só pelo .env da caixa ou SQL no store'}
                    </div>
                    {/* Aqui é onde a pessoa DECIDE conceder, então as tools ficam à
                        vista — não atrás de um tooltip. */}
                    <ul style={S.tools}>
                      {(q ? toolsQueCasam(t) : t.tools).map(n => <li key={n} style={S.toolItem}>{n}</li>)}
                    </ul>
                  </span>
                </label>
              ))}

              <details style={{ marginTop: 12 }} open={!!q && temasVisiveis.some(t => !temaCasa(t))}>
                <summary style={{ cursor: 'pointer', fontSize: 13 }}>
                  Exceções: uma tool avulsa, sem o tema inteiro
                </summary>
                <div style={{ ...S.sub, margin: '6px 0' }}>
                  Use quando alguém precisa de UMA tool e o tema traria junto o que ela não
                  deve ter. Some da lista se a tool sair do MCP.
                </div>
                {temasVisiveis.filter(t => toolsQueCasam(t).length).map(t => (
                  <div key={t.chave} style={{ marginTop: 8 }}>
                    <div style={{ ...S.mono, color: '#666' }}>{t.chave}</div>
                    {toolsQueCasam(t).map(tool => (
                      <label key={tool} style={{ ...S.linha, paddingLeft: 12 }}>
                        <input type="checkbox" disabled={salvando}
                               checked={marcados.has(`tool:${tool}`)}
                               onChange={() => alternar(`tool:${tool}`)} />
                        <span style={S.mono}>{tool}</span>
                        {marcados.has(t.chave) && (
                          <span style={{ ...S.sub, marginLeft: 6 }}>
                            (já vem pelo tema; marcar aqui é redundante)
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                ))}
              </details>

              <div style={{ marginTop: 14 }}>
                <label style={{ fontSize: 13 }}>
                  Motivo <span style={{ color: '#b91c1c' }}>*</span>
                  <div style={S.sub}>
                    Fica na trilha, que é append-only. É o que responde "por que fulano ganhou
                    isso" daqui a seis meses.
                  </div>
                  <input style={S.input} value={motivo} disabled={salvando}
                         onChange={ev => setMotivo(ev.target.value)}
                         placeholder="ex.: entrou no time de dados" />
                </label>
              </div>

              {recado?.tipo === 'erro' && (
                <div style={{ ...S.erro, marginTop: 10 }}>{recado.texto}</div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                <button style={S.btn} disabled={salvando}
                        onClick={() => setEditando(null)}>Cancelar</button>
                <button style={{ ...S.btnP, opacity: (!mudou || motivo.trim().length < 3 || salvando) ? .5 : 1 }}
                        disabled={!mudou || motivo.trim().length < 3 || salvando}
                        onClick={salvar}>
                  {salvando ? 'Salvando…' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <div style={{ ...S.sub, marginTop: 12 }}>
        Lido em {fmtData(dados.gerado_em)}
        {dados.eu ? <> · você é <span style={S.mono}>{dados.eu}</span></> : null}
      </div>
    </div>
  );
}
